import { useState, useRef } from 'react';
import { api } from '../../utils/api';
import { useAuth } from '../../hooks/useAuth';
import { useContact } from '../../hooks/useContact';
import { motion, AnimatePresence } from 'framer-motion';

export const SupportBubble = () => {
    const { user } = useAuth();
    const { submitContact, loading, success, resetStatus, error } = useContact();
    const [isOpen, setIsOpen] = useState(false);
    const [formData, setFormData] = useState({
        sujet: '',
        message: '',
        email: '',
        telephone: user?.telephone || '',
        fileUrl: ''
    });
    const [uploadLoading, setUploadLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Masquer pour le staff qui gère déjà les messages
    if (user && ['admin', 'collaborator'].includes(user.role)) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const res = await submitContact({
            prenom: user?.prenom || 'Visiteur',
            nom: user?.nom || 'Anonyme',
            email: user?.email || formData.email,
            telephone: formData.telephone,
            sujet: formData.sujet || 'Demande Support',
            message: formData.message,
            entreprise: user?.role === 'employer' ? 'Client Recruteur' : undefined,
            fileUrl: formData.fileUrl
        });

        if (res.success) {
            setFormData({ sujet: '', message: '', email: '', telephone: user?.telephone || '', fileUrl: '' });
            setTimeout(() => {
                setIsOpen(false);
                resetStatus();
            }, 3000);
        }
    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadLoading(true);
        try {
            const uploadData = new FormData();
            uploadData.append('file', file);
            const res = await api.post('/upload/image', uploadData);
            const fileUrl = res.url || res.data?.url || res;
            setFormData(prev => ({ ...prev, fileUrl: typeof fileUrl === 'string' ? fileUrl : fileUrl.url }));
        } catch (err) {
            console.error('Upload error:', err);
        } finally {
            setUploadLoading(false);
        }
    };

    const removeFile = () => {
        setFormData(prev => ({ ...prev, fileUrl: '' }));
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <>
            <div className="fixed bottom-6 right-6 z-[99999]">
                {/* Bubble Toggle Button */}
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-14 h-14 bg-black text-white rounded-full shadow-2xl flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-300 group cursor-pointer border-2 border-emerald-400/20"
                >
                    <i className={`${isOpen ? 'ri-close-line' : 'ri-chat-smile-2-line'} text-2xl group-hover:rotate-12 transition-transform`}></i>
                    {!isOpen && !success && (
                         <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full animate-pulse border-2 border-white"></span>
                    )}
                </button>

                {/* Chat Window */}
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            initial={{ opacity: 0, y: 20, scale: 0.9, transformOrigin: 'bottom right' }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 20, scale: 0.9 }}
                            className="absolute bottom-20 right-0 w-[350px] bg-white rounded-[2.5rem] shadow-[0_20px_60px_rgba(0,0,0,0.15)] border border-gray-100 overflow-hidden"
                        >
                            {/* Header */}
                            <div className="bg-[#1B3A2F] p-8 text-white relative">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16"></div>
                                <div className="relative z-10">
                                    <h3 className="text-xl font-black italic uppercase tracking-tight leading-none mb-1">Support Client</h3>
                                    <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest">Une question ? Nous sommes là.</p>
                                </div>
                            </div>

                            <div className="p-8">
                                {success ? (
                                    <div className="py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
                                        <div className="w-20 h-20 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-emerald-100 outline outline-4 outline-emerald-50">
                                            <i className="ri-checkbox-circle-fill text-4xl"></i>
                                        </div>
                                        <h4 className="text-lg font-black text-[#1B3A2F] uppercase italic">Message envoyé !</h4>
                                        <p className="text-sm text-gray-500 font-medium">Nous vous répondrons par e-mail dans les plus brefs délais.</p>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-5">
                                        {!user && (
                                            <div className="space-y-4">
                                                <p className="text-[10px] bg-gray-50 p-3 rounded-xl border border-gray-100 text-gray-600 font-medium italic">
                                                    <i className="ri-information-line mr-1 text-emerald-500"></i>
                                                    Connectez-vous pour un suivi personnalisé ou laissez votre email.
                                                </p>
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Email <span className="text-red-500">*</span></label>
                                                    <input
                                                        required
                                                        type="email"
                                                        placeholder="votre@email.com"
                                                        className="w-full px-5 py-3.5 bg-gray-50 rounded-2xl border-2 border-transparent focus:border-[#FFD700]/10 focus:bg-white focus:outline-none text-xs font-bold text-[#1B3A2F] transition-all"
                                                        value={formData.email}
                                                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Sujet</label>
                                            <input
                                                required
                                                type="text"
                                                placeholder="Comment pouvons-nous vous aider ?"
                                                className="w-full px-5 py-3.5 bg-gray-50 rounded-2xl border-2 border-transparent focus:border-[#FFD700]/10 focus:bg-white focus:outline-none text-xs font-bold text-[#1B3A2F] transition-all"
                                                value={formData.sujet}
                                                onChange={(e) => setFormData({...formData, sujet: e.target.value})}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Message</label>
                                            <textarea
                                                required
                                                rows={4}
                                                placeholder="Décrivez votre demande..."
                                                className="w-full px-5 py-3.5 bg-gray-50 rounded-2xl border-2 border-transparent focus:border-[#FFD700]/10 focus:bg-white focus:outline-none text-xs font-bold text-[#1B3A2F] transition-all resize-none"
                                                value={formData.message}
                                                onChange={(e) => setFormData({...formData, message: e.target.value})}
                                            ></textarea>
                                        </div>

                                        {/* File Attachment */}
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Pièce jointe (Optionnel)</label>
                                            <input 
                                                type="file" 
                                                ref={fileInputRef}
                                                onChange={handleFileUpload}
                                                className="hidden"
                                            />
                                            
                                            {!formData.fileUrl ? (
                                                <button
                                                    type="button"
                                                    onClick={() => fileInputRef.current?.click()}
                                                    disabled={uploadLoading}
                                                    className="w-full px-5 py-3 bg-emerald-50 rounded-2xl border-2 border-dashed border-emerald-100 flex items-center justify-center gap-3 text-[#1B3A2F] hover:bg-emerald-100 transition-all cursor-pointer group/file"
                                                >
                                                    {uploadLoading ? (
                                                        <div className="w-4 h-4 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
                                                    ) : (
                                                        <>
                                                            <i className="ri-attachment-line text-lg group-hover/file:rotate-45 transition-transform"></i>
                                                            <span className="text-[10px] font-black uppercase">Joindre un fichier</span>
                                                        </>
                                                    )}
                                                </button>
                                            ) : (
                                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-2xl border border-gray-100 animate-in slide-in-from-top-2 duration-300">
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <div className="w-8 h-8 bg-white rounded-xl flex items-center justify-center text-emerald-500 shadow-sm">
                                                            <i className="ri-file-check-line text-lg"></i>
                                                        </div>
                                                        <p className="text-[10px] font-black text-[#1B3A2F] truncate max-w-[150px]">Fichier prêt</p>
                                                    </div>
                                                    <button 
                                                        type="button"
                                                        onClick={removeFile}
                                                        className="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                                    >
                                                        <i className="ri-delete-bin-line"></i>
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {error && (
                                            <p className="text-[10px] text-red-500 font-bold bg-red-50 p-2 rounded-lg text-center">{error}</p>
                                        )}

                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full py-4 bg-[#1B3A2F] text-white rounded-2xl text-[11px] font-black uppercase tracking-[0.2em] shadow-xl shadow-emerald-900/10 hover:bg-[#FFD700] hover:text-[#1B3A2F] transition-all duration-500 disabled:opacity-50 flex items-center justify-center gap-3 cursor-pointer"
                                        >
                                            {loading ? (
                                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                            ) : (
                                                <>
                                                    <i className="ri-send-plane-2-fill text-lg"></i>
                                                    Envoyer ma demande
                                                </>
                                            )}
                                        </button>
                                        
                                        <p className="text-[8px] text-center text-gray-400 uppercase tracking-widest font-black italic">
                                            Support Travail Global • Réponse sous 24h
                                        </p>
                                    </form>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </>
    );
};
};
